import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useState } from 'react';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Skeleton } from '~components/Skeleton/Skeleton';
import useBilling from '~hooks/useBilling/useBilling';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useBillingInvoicesInfiniteQuery from '~queries/useBillingInvoicesInfiniteQuery';
import useBillingSummaryQuery from '~queries/useBillingSummaryQuery';
import { useStores } from '~store';
import usePopupSession from '~utils/hooks/usePopupSession';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import BillingEmptyState from './components/BillingEmptyState';
import BillingSummaryStrip from './components/BillingSummaryStrip';
import InvoiceCard from './components/InvoiceCard';

type SessionKind = 'checkout' | 'portal';

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={(theme) => ({
        color: theme.brandV2.colors.treadGray4,
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.09em',
        mb: 1,
        textTransform: 'uppercase',
      })}
    >
      {children}
    </Typography>
  );
}

function BillingSkeleton() {
  return (
    <Stack spacing={2} data-test-id="settings-billing-skeleton">
      <Skeleton variant="rectangular" height={72} />
      <Skeleton variant="rectangular" height={64} />
      <Skeleton variant="rectangular" height={64} />
      <Skeleton variant="rectangular" height={64} />
    </Stack>
  );
}

export const BillingManagement = observer(() => {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { isError, isPending, refetch, summary } = useBillingSummaryQuery(companyId);
  const { createCheckoutSession, createPortalSession } = useBilling();
  const [mintingSession, setMintingSession] = useState<SessionKind | null>(null);
  const { openBlankPopup, watchPopupClose } = usePopupSession();

  const isConfigured = summary?.configured === true;
  const invoicesQuery = useBillingInvoicesInfiniteQuery(companyId, isConfigured);

  const openMintedSession = useCallback(
    async (kind: SessionKind) => {
      if (mintingSession) {
        return;
      }

      const popup = openBlankPopup(t('error_messages.billing.failed_to_open_popup'));
      if (!popup) {
        return;
      }

      setMintingSession(kind);
      const session =
        kind === 'portal' ? await createPortalSession() : await createCheckoutSession();
      setMintingSession(null);

      if (!session || popup.closed) {
        popup.close();

        return;
      }

      popup.location.href = session.url;

      // A verified ACH mandate (Checkout) or a payment-method change made in the
      // billing Portal both flip bank_account_on_file server-side; refetch when
      // either popup closes so the chip reflects it without a reload.
      watchPopupClose(popup, refetch);
    },
    [
      createCheckoutSession,
      createPortalSession,
      mintingSession,
      openBlankPopup,
      refetch,
      watchPopupClose,
    ],
  );

  const showBankSetupCta =
    isConfigured && summary.achEligible && !summary.bankAccountOnFile;

  const headerActions = isConfigured ? (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      {summary.bankAccountOnFile && (
        <Chip
          data-test-id="settings-billing-bank-on-file-chip"
          icon={<CheckCircleOutlined />}
          label={t('billing.tab.bank_account_on_file')}
          size="small"
          sx={(theme) => ({
            bgcolor: theme.semantic.statusSuccessBg,
            color: theme.semantic.statusSuccess,
            fontWeight: 600,
            '& .MuiChip-icon': { color: theme.semantic.statusSuccess },
          })}
        />
      )}
      {showBankSetupCta && (
        <SmallLoadingButton
          color="brandV2Black"
          data-ui="page-settings-billing__setup-bank-payments-button"
          disabled={mintingSession !== null}
          loading={mintingSession === 'checkout'}
          onClick={() => openMintedSession('checkout')}
          variant="outlined"
          sx={{ borderColor: 'divider', color: 'text.primary' }}
        >
          {t('billing.tab.setup_bank_payments')}
        </SmallLoadingButton>
      )}
      <SmallLoadingButton
        color="brandV2Yellow"
        data-ui="page-settings-billing__manage-billing-button"
        disabled={mintingSession !== null}
        loading={mintingSession === 'portal'}
        onClick={() => openMintedSession('portal')}
      >
        {t('billing.tab.manage_billing')}
      </SmallLoadingButton>
    </Stack>
  ) : undefined;

  let content: ReactNode;

  if (isPending) {
    content = <BillingSkeleton />;
  } else if (isError) {
    content = (
      <BillingEmptyState
        action={
          <SmallButton
            color="brandV2Black"
            data-ui="page-settings-billing__retry-button"
            onClick={() => refetch()}
            variant="outlined"
            sx={{ borderColor: 'divider', color: 'text.primary' }}
          >
            {t('actions.retry')}
          </SmallButton>
        }
        dataTestId="settings-billing-error-state"
        description={t('billing.tab.error_description')}
        icon={<ErrorOutlineOutlined />}
        title={t('billing.tab.error_title')}
      />
    );
  } else if (!summary) {
    content = (
      <BillingEmptyState
        dataTestId="settings-billing-preparing-state"
        description={t('billing.tab.preparing_description')}
        icon={<HourglassEmptyOutlined />}
        title={t('billing.tab.preparing_title')}
      />
    );
  } else if (!summary.configured) {
    content = (
      <BillingEmptyState
        dataTestId="settings-billing-not-configured-state"
        description={t('billing.tab.not_configured_description')}
        icon={<AccountBalanceOutlined />}
        title={t('billing.tab.not_configured_title')}
      />
    );
  } else {
    content = (
      <Stack spacing={3}>
        <BillingSummaryStrip summary={summary} />

        <Box data-test-id="settings-billing-open-invoices">
          <SectionHeading>{t('billing.tab.open_invoices')}</SectionHeading>
          {summary.openInvoices.length === 0 ? (
            <BillingEmptyState
              dataTestId="settings-billing-no-open-invoices"
              description={t('billing.tab.no_open_invoices_description')}
              icon={<ReceiptLongOutlined />}
              title={t('billing.tab.no_open_invoices_title')}
            />
          ) : (
            <Stack spacing={1}>
              {summary.openInvoices.map((invoice) => (
                <InvoiceCard invoice={invoice} key={invoice.id} variant="open" />
              ))}
            </Stack>
          )}
        </Box>

        <Box data-test-id="settings-billing-payment-history">
          <SectionHeading>{t('billing.tab.payment_history')}</SectionHeading>
          <Stack spacing={1}>
            {invoicesQuery.invoices.map((invoice) => (
              <InvoiceCard invoice={invoice} key={invoice.id} variant="history" />
            ))}
          </Stack>
          {invoicesQuery.isError ? (
            <Typography
              sx={(theme) => ({ color: theme.semantic.statusError, fontSize: 13, mt: 1 })}
            >
              {t('error_messages.billing.failed_to_fetch_invoices')}
            </Typography>
          ) : invoicesQuery.isFetching || invoicesQuery.isFetchingNextPage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <LoadingSpinner isVisible loadingIndicatorSize={16} />
            </Box>
          ) : invoicesQuery.hasMore ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <SmallButton
                color="brandV2Black"
                data-ui="page-settings-billing__load-more-button"
                onClick={invoicesQuery.fetchMore}
                variant="outlined"
                sx={{ borderColor: 'divider', color: 'text.primary' }}
              >
                {t('billing.tab.load_more')}
              </SmallButton>
            </Box>
          ) : (
            <DividerWithText
              sx={{ mt: 1 }}
              text={
                invoicesQuery.invoices.length
                  ? t('billing.tab.all_invoices_loaded')
                  : t('billing.tab.no_paid_invoices')
              }
            />
          )}
        </Box>
      </Stack>
    );
  }

  return (
    <Box data-test-id="settings-billing">
      <SettingsPageHeader
        action={headerActions}
        description={t('settings.page_descriptions.billing')}
        title={t('billing.tab.title')}
      />
      {content}
    </Box>
  );
});
